import { createElement, type MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
  Slide,
  toast,
  ToastContainer,
  type CloseButtonProps,
  type ToastContent,
  type ToastOptions,
} from 'react-toastify';
import { CButton } from '@/components/CButton';
import { Icon } from '@/components/Icon';
import { type RootState } from '@/store/store';

import 'react-toastify/dist/ReactToastify.css';
import './index.scss';

type ToastMessage = ToastContent<unknown>;

// MEU toasts: no status icon and no progress bar; the tone is the colour of the start edge.
const defaultToastOptions: ToastOptions = {
  position: 'top-right',
  closeOnClick: true,
  draggable: true,
  pauseOnHover: true,
  pauseOnFocusLoss: true,
  autoClose: 2500,
  hideProgressBar: true,
  icon: false,
};

const createToastOptions = (options?: ToastOptions): ToastOptions => ({
  ...defaultToastOptions,
  ...options,
});

const toastSuccess = (content?: ToastMessage, options?: ToastOptions) => {
  return toast.success(content, createToastOptions({ ...options }));
};

const toastError = (content?: ToastMessage, options?: ToastOptions) => {
  return toast.error(content, createToastOptions({ ...options }));
};

const toastInfo = (content?: ToastMessage, options?: ToastOptions) => {
  return toast.info(content, createToastOptions({ ...options }));
};

const toastWarning = (content?: ToastMessage, options?: ToastOptions) => {
  return toast.warning(content, createToastOptions({ ...options }));
};

const AppToast = () => {
  const { t } = useTranslation(['common']);
  const themeMode = useSelector((state: RootState) => state.theme.themeMode);

  return createElement(ToastContainer, {
    ...defaultToastOptions,
    theme: themeMode,
    transition: Slide,
    closeButton: ({ closeToast }: CloseButtonProps) =>
      createElement(CButton, {
        iconOnly: true,
        variant: 'ghost',
        className: 'c-toast__close',
        startIcon: createElement(Icon, { name: 'close', size: 16 }),
        'aria-label': t('common:common.close'),
        // Same as react-toastify's own button: the click stays off the toast and counts as the user's.
        onClick: (event: MouseEvent) => {
          event.stopPropagation();
          closeToast(true);
        },
      }),
  });
};

export { AppToast, toastError, toastInfo, toastSuccess, toastWarning };
